'use client'

import { HeaderSearch, InputSearch } from '@dafiti/closet-ui'
import { useEffect, useRef, useState } from 'react'
import { useResponsive } from '@dafiti/closet-ui/hooks'
import { SearchList } from './SearchList/SearchList'
import { useMenuStore } from '@/store/menu.slice'
import { useSearchStore } from '@/store/search.slice'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { useMessages } from '@/i18n/useMessages'
import {
  parseSearchString,
  sanitizeSearchString,
  setSearchActionGA,
} from '@/helpers/search'
import { useSearch } from '@/hooks/useSearch'
import { DefaultFormComponent } from '@/components/DefaultForm'

export const Search = () => {
  const maxCharactersForSearch = 60

  const searchFieldRef = useRef<HTMLInputElement>(null)

  const {
    options,
    FormComponent = DefaultFormComponent,
    router,
  } = useAppLayoutContext()
  const t = useMessages()

  const isSearchSuggestionEnabled =
    !!options.features?.includes('search_suggestion')
  const isLeanCatalogEnabled = !!options.features?.includes('lean_catalog')
  const catalogUrl = isLeanCatalogEnabled ? `/s` : `/catalog/`

  const isCatalogPage =
    !!router?.pathname &&
    (router.pathname.startsWith('/s/') ||
      router.pathname.startsWith('/b/') ||
      router.pathname.startsWith('/c/'))

  const { isMobile } = useResponsive()

  const [searchInputValue, setSearchInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const isMenuOpen = useMenuStore((state) => state.isOpen)
  const closeMenu = useMenuStore((state) => state.close)
  const { isSearchOpen, openSearch, closeSearch } = useSearchStore()

  const {
    items,
    isVisible,
    type,
    addHistoryItem,
    removeHistoryItem,
    isLoading,
  } = useSearch({
    query: searchInputValue,
    isFocused,
    maxCharacters: maxCharactersForSearch,
  })

  useEffect(() => {
    setIsFocused(false)
    // Drop focus from the field after navigating. On desktop the suggestion
    // click holds focus on the input (preventDefault on pointerdown), so it
    // would otherwise stay focused after the route changes.
    searchFieldRef.current?.blur()
  }, [router?.pathname])

  const handleCloseDropdownOnFocusLeave = (
    e: React.FocusEvent<HTMLDivElement>,
  ) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      // Defer the close so a click on a suggestion/recent item commits its
      // navigation first. Closing synchronously on blur unmounts the list —
      // and the clicked link with it — before the click/mousedown handler
      // runs, which silently drops the navigation.
      setTimeout(() => setIsFocused(false), 0)
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const currentSearchValue = isSearchSuggestionEnabled
      ? searchInputValue
      : searchFieldRef.current?.value || ''

    if (!currentSearchValue) {
      e.preventDefault()
      return
    }

    setSearchActionGA('typeSearch')

    if (isSearchSuggestionEnabled) {
      addHistoryItem(currentSearchValue)
    }

    if (isLeanCatalogEnabled) {
      e.preventDefault()
      router?.push(`/s/${sanitizeSearchString(currentSearchValue)}`)
    }
  }

  // Prevent background scroll while the search dropdown is open
  useEffect(() => {
    document.body.style.overflow = isFocused ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFocused])

  useEffect(() => {
    const isSearchRoute = router?.pathname?.startsWith('/s/')

    if (!router?.pathname || !isLeanCatalogEnabled || !isSearchRoute) return

    const pathSegment = router.pathname.split('/s/').pop()
    const value = pathSegment ? parseSearchString(pathSegment) : ''

    setSearchInputValue(value)
  }, [router?.pathname, isLeanCatalogEnabled])

  const handleOpenSearch = () => {
    openSearch()
    if (isMenuOpen) closeMenu()
  }

  return (
    <>
      <HeaderSearch
        open={isSearchOpen}
        onOpenSearch={() => {
          handleOpenSearch()
          searchFieldRef.current?.focus()
        }}
        onCloseSearch={() => {
          closeSearch()
          setIsFocused(false)
        }}
        onBlur={handleCloseDropdownOnFocusLeave}
        initiallyOpen={
          (isLeanCatalogEnabled && isCatalogPage && isMobile) || false
        }
        onInitialOpen={handleOpenSearch}
      >
        <div className="w-full overflow-hidden p-[2px] lg:overflow-visible lg:p-0">
          <FormComponent
            action={catalogUrl}
            method="GET"
            className="w-full"
            onSubmit={handleFormSubmit}
          >
            {isSearchSuggestionEnabled ? (
              <InputSearch
                ref={searchFieldRef}
                variant="filled"
                className="w-full"
                size="sm"
                name="q"
                placeholder={t.search.placeholder}
                aria-label={t.search.ariaLabel}
                showBackButton
                backButtonAriaLabel={t.search.backButtonAriaLabel}
                submitAriaLabel={t.search.submit}
                clearAriaLabel={t.search.clearAriaLabel}
                value={searchInputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchInputValue(e.target.value)
                }
                onFocus={() => setIsFocused(true)}
                onClear={() => setSearchInputValue('')}
              />
            ) : (
              <InputSearch
                ref={searchFieldRef}
                variant="filled"
                className="w-full"
                size="sm"
                name="q"
                placeholder={t.search.placeholder}
                aria-label={t.search.ariaLabel}
                showBackButton
                backButtonAriaLabel={t.search.backButtonAriaLabel}
                submitAriaLabel={t.search.submit}
                clearAriaLabel={t.search.clearAriaLabel}
                defaultValue={searchInputValue}
              />
            )}

            <input type="hidden" name="wtqs" value="1" />
          </FormComponent>

          {isSearchSuggestionEnabled &&
            isVisible &&
            searchInputValue.length <= maxCharactersForSearch && (
              <SearchList
                items={items}
                type={type}
                query={searchInputValue}
                onClickItem={addHistoryItem}
                isLeanCatalogEnabled={isLeanCatalogEnabled}
                onRemoveHistoryItem={removeHistoryItem}
                onFillInput={(term) => setSearchInputValue(term)}
                isLoading={isLoading}
              />
            )}
        </div>
      </HeaderSearch>

      {isFocused && (
        <div
          className="absolute inset-0 top-full z-40 h-[100vh] bg-black/65"
          data-testid="search-overlay"
          aria-hidden="true"
        />
      )}
    </>
  )
}
